import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core';
import CustomTable from '../components/CustomTable';
import { Edit, Settings, StarBorder } from '@material-ui/icons';
import CategoryDrawer from './CategoryDrawer';
import { etichettaCadenza } from '../../../utils/constants';

const useStyles = makeStyles((theme) => ({
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    width: '100%',
  },
  tableContainer: {
    width: '85%',
    maxWidth: '1520px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    margin: '2% 0',
    textAlign: 'left',
  },
  renderValueStyle: {
    display: 'inline-block',
    width: '240px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  },
  customItemWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  groupHeader: {
    fontWeight: 600,
    fontSize: '0.8rem',
    opacity: 0.7,
    cursor: 'default',
  },
  categoryButton: {
    border: '2px solid',
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      border: '2px solid',
    },
  },
}));

const cadenzeDisponibili = [
  'GIORNALIERA',
  'SETTIMANALE',
  'MENSILE',
  'ANNUALE',
  'ALTRO',
];

const SchedaTable = ({
  categorieSchede,
  handleCadenzaChange,
  handleCalendarizzatoChange,
  handleCategoriaChange,
  handleDeleteCategory,
  handlePeriodicoChange,
  isListaOrariDisabled,
  loading,
  openAssociazioneModal,
  openNuovaCategoriaModal,
  openOrarioModal,
  scheda,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const schedaColumns = [
    {
      Header: 'Categoria Scheda',
      id: 'categoriaScheda',
      width: 360,
      accessor: (d) => d,
      Cell: ({ row }) => {
        const currentValue = row.categoriaScheda.categoria || 'SCHEDA_GENERICA';

        const std = categorieSchede.filter((c) => !c.isCustom);
        const custom = categorieSchede.filter((c) => c.isCustom);

        return (
          categorieSchede.length > 0 && (
            <Select
              value={currentValue}
              displayEmpty
              onChange={(event) => handleCategoriaChange(event)}
              style={{ marginTop: 10, width: '80%' }}
              renderValue={(selected) => {
                if (selected === 'PERSONALIZZATA') {
                  return (
                    <div className={classes.renderValueStyle}>
                      {row.categoriaScheda.categoriaPersonalizzataNome ||
                        'Categoria personalizzata'}
                    </div>
                  );
                }

                const item = categorieSchede.find(
                  (cat) => cat.value === selected,
                );
                return item ? (
                  <div className={classes.renderValueStyle} title={item.label}>
                    {item.label}
                  </div>
                ) : (
                  'Seleziona una categoria'
                );
              }}
            >
              <MenuItem value="" disabled>
                Seleziona una categoria
              </MenuItem>

              {/* Nasconde "PERSONALIZZATO" dalla lista. Questo serve solo per
                        evitare errori di "out-of-range value", ma l'opzione non è
                        selezionabile.*/}
              <MenuItem
                value="PERSONALIZZATA"
                disabled
                style={{ display: 'none' }}
              >
                Categoria personalizzata
              </MenuItem>

              <MenuItem disabled className={classes.groupHeader}>
                Standard
              </MenuItem>
              {std
                .filter((categoria) => categoria.value !== 'PERSONALIZZATA')
                .map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}

              {custom.length > 0 && (
                <MenuItem disabled className={classes.groupHeader}>
                  Personalizzate
                </MenuItem>
              )}
              {custom.map((c) => (
                <MenuItem key={c.id ?? c.value} value={c.value}>
                  <StarBorder
                    style={{ fontSize: 16, marginRight: 8, opacity: 0.7 }}
                  />
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          )
        );
      },
    },
    {
      Header: 'Scheda Calendarizzata',
      id: 'calendarizzato',
      width: 240,
      accessor: (d) => d.calendarizzato,
      Cell: ({ row }) => (
        <Select
          value={row.calendarizzato ?? false}
          onChange={(event) => handleCalendarizzatoChange(event)}
          style={{ marginTop: 10, width: '80%' }}
        >
          <MenuItem key={1} value={true}>
            Sì
          </MenuItem>
          <MenuItem key={2} value={false}>
            No
          </MenuItem>
        </Select>
      ),
    },
    {
      Header: 'Periodica',
      id: 'periodico',
      width: 180,
      accessor: (d) => d.periodico,
      Cell: ({ row }) => (
        <Checkbox
          disabled={!row.calendarizzato}
          checked={row.periodico}
          onChange={handlePeriodicoChange}
        />
      ),
    },
    {
      Header: 'Cadenza',
      id: 'cadenza',
      width: 360,
      accessor: (d) => d.cadenza,
      Cell: ({ row }) => (
        <Select
          style={{ marginTop: 10, width: '80%' }}
          value={row.cadenza ?? 'ALTRO'}
          onChange={(event) => handleCadenzaChange(event)}
        >
          {cadenzeDisponibili.map((item, index) => {
            if (row.calendarizzato && item !== 'ALTRO') {
              return (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              );
            } else if (!row.calendarizzato && item === 'ALTRO') {
              return (
                <MenuItem key={index} value={'ALTRO'}>
                  {etichettaCadenza(item)}
                </MenuItem>
              );
            }
            return null;
          })}
        </Select>
      ),
    },
    {
      Header: 'Lista Orari',
      id: 'listaOrari',
      width: 180,
      accessor: (d) => d,
      Cell: ({ row }) => (
        <IconButton
          disabled={
            isListaOrariDisabled ||
            scheda[0].cadenza === 'ALTRO' ||
            row.periodico
          }
          onClick={openOrarioModal}
          style={{ marginTop: 10 }}
          size="small"
          title="Modifica orario"
        >
          <Edit />
        </IconButton>
      ),
    },
    {
      Header: 'Associazioni',
      id: 'associazioni',
      width: 180,
      accessor: (d) => d,
      Cell: ({ row }) => (
        <IconButton
          onClick={openAssociazioneModal}
          style={{ marginTop: 10 }}
          size="small"
          title="Modifica associazioni"
        >
          <Edit />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={classes.tableWrapper}>
      <div className={classes.tableContainer}>
        <div className={classes.headerRow}>
          <Typography className={classes.pageTitle} variant="h4">
            Scheda
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Settings color="primary" />}
            onClick={() => setDrawerOpen(true)}
            disabled={loading}
            className={classes.categoryButton}
          >
            Gestisci categorie
          </Button>
        </div>
        <CustomTable
          records={scheda}
          columns={schedaColumns}
          filterable={false}
          resizable={true}
          defaultPageSize={1}
          sortable={false}
          loading={loading}
          showPagination={false}
          tableStyle={{ boxShadow: theme.shadows[3] }}
        />
      </div>
      <CategoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customCategories={categorieSchede.filter((c) => c.isCustom)}
        onCreate={() => {
          setDrawerOpen(false);
          openNuovaCategoriaModal();
        }}
        onDelete={(cat) => {
          setDrawerOpen(false);
          handleDeleteCategory(cat);
        }}
      />
    </div>
  );
};

SchedaTable.propTypes = {
  categorieSchede: PropTypes.array.isRequired,
  handleCadenzaChange: PropTypes.func.isRequired,
  handleCalendarizzatoChange: PropTypes.func.isRequired,
  handleCategoriaChange: PropTypes.func.isRequired,
  handleDeleteCategory: PropTypes.func.isRequired,
  handlePeriodicoChange: PropTypes.func.isRequired,
  isListaOrariDisabled: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  openAssociazioneModal: PropTypes.func.isRequired,
  openNuovaCategoriaModal: PropTypes.func.isRequired,
  openOrarioModal: PropTypes.func.isRequired,
  scheda: PropTypes.array.isRequired,
};

export default SchedaTable;
